import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import { COLORS } from "../../utils/colors";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const STATUS_ORDER = ["active", "inactive", "cancelled"];
const STATUS_COLORS = {
  active: "#2e7d32",
  inactive: "#ed6c02",
  cancelled: "#9e9e9e",
};
const REASON_COLORS = {
  UTM: COLORS.priorityBlue.hex,
  Partners: "#5c6bc0",
  Other: "#90a4ae",
};
const SOURCE_PALETTE = [
  "#0F083E",
  "#3949ab",
  "#00897b",
  "#6d4c41",
  "#c62828",
  "#1565c0",
  "#6a1b9a",
  "#f9a825",
];

const chartWrapSx = {
  p: 1.5,
  flex: "1 1 240px",
  minWidth: 240,
  maxWidth: 420,
  height: 230,
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "right",
      labels: { boxWidth: 10, font: { size: 11 } },
    },
  },
};

const barOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { ticks: { precision: 0, font: { size: 10 } }, grid: { display: false } },
    y: { ticks: { font: { size: 10 } }, grid: { display: false } },
  },
};

function countMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    const label = row._id || "unknown";
    map[label] = (map[label] || 0) + (row.count || 0);
  });
  return map;
}

const MembershipAnalyticsCharts = ({ summary, onSelectSource }) => {
  if (!summary || !summary.members) return null;

  const statusCounts = countMap(summary.byStatus);
  const statusLabels = [
    ...STATUS_ORDER.filter((s) => statusCounts[s]),
    ...Object.keys(statusCounts).filter((s) => !STATUS_ORDER.includes(s)),
  ];

  const typeCounts = { UTM: 0, Partners: 0, Other: 0 };
  (summary.byType || summary.byReason || []).forEach((row) => {
    const key = row._id === "UTM" || row._id === "Partners" || row._id === "Other" ? row._id : "Other";
    typeCounts[key] += row.count || 0;
  });
  const typeLabels = Object.keys(typeCounts).filter((k) => typeCounts[k] > 0);

  const sourceRows = summary.bySource || [];
  const sourceLabels = sourceRows.map((r) => {
    const name = r._id == null ? "" : String(r._id).trim();
    return name || "None";
  });
  const sourceValues = sourceRows.map((r) => r.count || 0);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "calc(100vw - 200px)",
        alignSelf: "flex-start",
        mx: "auto",
      }}
    >
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Paper sx={chartWrapSx} elevation={1}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Subscription status
          </Typography>
          <Box sx={{ height: 170 }}>
            <Doughnut
              options={doughnutOptions}
              data={{
                labels: statusLabels,
                datasets: [
                  {
                    data: statusLabels.map((s) => statusCounts[s]),
                    backgroundColor: statusLabels.map((s) => STATUS_COLORS[s] || "#90caf9"),
                  },
                ],
              }}
            />
          </Box>
        </Paper>
        <Paper sx={chartWrapSx} elevation={1}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Source type
          </Typography>
          <Box sx={{ height: 170 }}>
            <Doughnut
              options={doughnutOptions}
              data={{
                labels: typeLabels,
                datasets: [
                  {
                    data: typeLabels.map((s) => typeCounts[s]),
                    backgroundColor: typeLabels.map((s) => REASON_COLORS[s]),
                  },
                ],
              }}
            />
          </Box>
        </Paper>
        <Paper sx={{ ...chartWrapSx, maxWidth: 520, flex: "1 1 280px" }} elevation={1}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Top sources
          </Typography>
          <Box sx={{ height: 170 }}>
            <Bar
              options={{
                ...barOptions,
                onClick: (_evt, elements) => {
                  if (!onSelectSource || !elements.length) return;
                  const idx = elements[0].index;
                  const raw = sourceRows[idx] && sourceRows[idx]._id;
                  const name = raw == null ? "" : String(raw);
                  onSelectSource(name.trim() ? name : "");
                },
              }}
              data={{
                labels: sourceLabels,
                datasets: [
                  {
                    data: sourceValues,
                    backgroundColor: sourceLabels.map(
                      (_l, i) => SOURCE_PALETTE[i % SOURCE_PALETTE.length]
                    ),
                  },
                ],
              }}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default MembershipAnalyticsCharts;
